import * as React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDomains } from '../state/domain-state';
import { useSubdomains } from '../state/subdomain-state';
import { useAssignments, type CreateAssignmentInput, type DeleteAssignmentInput } from '../state/assignment-state';
import { useBreadcrumb } from '../state/breadcrumb-state';
import { useToast } from '../state/toast-state';
import { Button } from '../components/ui/button';
import { Spinner } from '../components/ui/spinner';
import { EmptyState } from '../components/ui/empty-state';
import { ErrorState } from '../components/ui/error-state';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import { Card, CardContent } from '../components/ui/card';
import { UserAutocomplete } from '../components/users/UserAutocomplete';
import { useUserResolve } from '../hooks/use-user-resolve';
import type { DirectoryUser } from '../hooks/use-user-search';
import { ApiErrorResponse } from '../services/api-client';

/**
 * SubdomainDetailPage — Displays subdomain metadata and its assignment list.
 * Supports assigning users via UserAutocomplete and removing assignments with
 * optimistic delete + revert on failure.
 *
 * Validates: Requirements 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 3.8, 4.1, 4.5, 5.1, 5.2, 5.3, 5.4, 5.5, 7.1, 7.2, 7.3
 */

export function SubdomainDetailPage(): React.JSX.Element {
  const { domainId, subdomainId } = useParams<{ domainId: string; subdomainId: string }>();
  const { state: domainState, loadDomains } = useDomains();
  const { state: subdomainState, loadSubdomains } = useSubdomains();
  const { state: assignmentState, loadAssignments, createAssignment, deleteAssignment } = useAssignments();
  const { setSegments } = useBreadcrumb();
  const { showToast } = useToast();
  const { resolvedUsers, isLoading: isResolving, resolve } = useUserResolve();

  const [showAssignForm, setShowAssignForm] = React.useState(false);
  const [confirmRemove, setConfirmRemove] = React.useState<{ userId: string; email: string } | null>(null);

  // Load domains if not already loaded
  React.useEffect(() => {
    if (domainState.domains.length === 0 && !domainState.isLoading) {
      loadDomains();
    }
  }, [domainState.domains.length, domainState.isLoading, loadDomains]);

  // Load subdomains for this domain
  React.useEffect(() => {
    if (domainId) {
      loadSubdomains(domainId);
    }
  }, [domainId, loadSubdomains]);

  // Load assignments for this subdomain
  React.useEffect(() => {
    if (subdomainId) {
      loadAssignments(subdomainId);
    }
  }, [subdomainId, loadAssignments]);

  // Resolve missing display names when assignments load
  React.useEffect(() => {
    if (assignmentState.assignments.length > 0) {
      // Collect all user IDs that need resolution: assigned users + assignedBy admins
      const idsToResolve = new Set<string>();

      for (const a of assignmentState.assignments) {
        if (!a.displayName) {
          idsToResolve.add(a.userId);
        }
        // Also resolve assignedBy if it looks like a UUID (not an email)
        if (a.assignedBy && !a.assignedBy.includes('@')) {
          idsToResolve.add(a.assignedBy);
        }
      }

      if (idsToResolve.size > 0) {
        resolve(Array.from(idsToResolve));
      }
    }
  }, [assignmentState.assignments, resolve]);

  // Find domain by ID
  const domain = React.useMemo(
    () => domainState.domains.find((d) => d.domainId === domainId) ?? null,
    [domainState.domains, domainId],
  );

  // Find subdomain by ID
  const subdomain = React.useMemo(
    () => subdomainState.subdomains.find((s) => s.subdomainId === subdomainId) ?? null,
    [subdomainState.subdomains, subdomainId],
  );

  // Set breadcrumb segments
  React.useEffect(() => {
    if (domain && subdomain) {
      setSegments([
        { label: 'Domains', href: '/admin-console/domains' },
        { label: domain.domainName, href: `/admin-console/domains/${domain.domainId}` },
        { label: subdomain.subdomainLabel },
      ]);
    }
    return () => {
      setSegments([]);
    };
  }, [domain, subdomain, setSegments]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleUserSelect = React.useCallback(
    (user: DirectoryUser): void => {
      if (!subdomainId) return;
      const input: CreateAssignmentInput = {
        subdomainId,
        userId: user.id,
        displayName: user.displayName,
        mail: user.mail ?? undefined,
      };
      createAssignment(input)
        .then(() => {
          setShowAssignForm(false);
          showToast('User assigned successfully.', 'success');
        })
        .catch((err: unknown) => {
          // On 502 errors, keep form open and show error toast
          if (err instanceof ApiErrorResponse && err.status === 502) {
            showToast('Directory service unavailable. Assignment was not created.', 'error');
          } else {
            showToast('Failed to create assignment.', 'error');
          }
          // Form stays open — do not call setShowAssignForm(false)
        });
    },
    [subdomainId, createAssignment, showToast],
  );

  const handleUserClear = React.useCallback((): void => {
    // No-op: autocomplete cleared, nothing to do
  }, []);

  const handleShowAssignForm = React.useCallback((): void => {
    setShowAssignForm(true);
  }, []);

  const handleHideAssignForm = React.useCallback((): void => {
    setShowAssignForm(false);
  }, []);

  const handleRemoveClick = React.useCallback((userId: string, email: string): void => {
    setConfirmRemove({ userId, email });
  }, []);

  const handleConfirmRemove = React.useCallback(async (): Promise<void> => {
    if (!subdomainId || !confirmRemove) return;
    const input: DeleteAssignmentInput = {
      subdomainId,
      userId: confirmRemove.userId,
    };
    setConfirmRemove(null);

    // Optimistic delete is handled by the state provider
    await deleteAssignment(input);

    // Check if the state reverted (error)
    if (assignmentState.error) {
      showToast('Failed to remove assignment. Changes reverted.', 'error');
    } else {
      showToast('Assignment removed successfully.', 'success');
    }
  }, [subdomainId, confirmRemove, deleteAssignment, assignmentState.error, showToast]);

  const handleCancelRemove = React.useCallback((): void => {
    setConfirmRemove(null);
  }, []);

  const handleRetryAssignments = React.useCallback((): void => {
    if (subdomainId) {
      loadAssignments(subdomainId);
    }
  }, [subdomainId, loadAssignments]);

  // ─── Helpers: Resolve display for assignment rows ────────────────────────────

  /**
   * Returns the display name for an assignment row.
   * Priority: stored displayName > resolved displayName > email > userId
   */
  const getDisplayName = React.useCallback(
    (assignment: { userId: string; email: string; displayName?: string }): string => {
      if (assignment.displayName) {
        return assignment.displayName;
      }
      const resolved = resolvedUsers[assignment.userId];
      if (resolved) {
        return resolved.displayName;
      }
      return assignment.email || assignment.userId;
    },
    [resolvedUsers],
  );

  /**
   * Returns secondary text (mail) for an assignment row.
   * Priority: stored mail (from assignment) > resolved mail > email field > null
   */
  const getSecondaryText = React.useCallback(
    (assignment: { userId: string; email: string; displayName?: string; mail?: string }): string | null => {
      // If we have a resolved or stored display name, show email as secondary
      if (assignment.mail) {
        return assignment.mail;
      }
      const resolved = resolvedUsers[assignment.userId];
      if (resolved?.mail) {
        return resolved.mail;
      }
      // If displayName is being shown, use email as secondary
      if (assignment.displayName || resolvedUsers[assignment.userId]) {
        return assignment.email || null;
      }
      return null;
    },
    [resolvedUsers],
  );

  /**
   * Returns whether this assignment row is still being resolved.
   */
  const isRowResolving = React.useCallback(
    (assignment: { userId: string; displayName?: string }): boolean => {
      return !assignment.displayName && !(assignment.userId in resolvedUsers) && isResolving;
    },
    [resolvedUsers, isResolving],
  );

  // ─── Render: Loading ─────────────────────────────────────────────────────────

  if (domainState.isLoading || subdomainState.isLoading) {
    return <Spinner label="Loading subdomain details..." />;
  }

  // ─── Render: Domain Error ────────────────────────────────────────────────────

  if (domainState.error) {
    return <ErrorState message={domainState.error} onRetry={loadDomains} />;
  }

  if (subdomainState.error && !subdomain) {
    return (
      <ErrorState
        message={subdomainState.error}
        onRetry={() => { if (domainId) loadSubdomains(domainId); }}
      />
    );
  }

  // ─── Render: 404 ─────────────────────────────────────────────────────────────

  if (!subdomain) {
    return (
      <section aria-labelledby="subdomain-not-found-heading" className="flex flex-col items-center gap-4 py-12">
        <h2 id="subdomain-not-found-heading" className="text-xl font-semibold text-foreground">
          Subdomain Not Found
        </h2>
        <p className="text-sm text-muted-foreground">
          The subdomain you are looking for does not exist or has been removed.
        </p>
        <Link
          to={domainId ? `/admin-console/domains/${domainId}` : '/admin-console/domains'}
          className="text-sm font-medium text-primary underline hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          ← Back to Domain
        </Link>
      </section>
    );
  }

  const parentDomainName = domain?.domainName ?? 'Unknown Domain';

  return (
    <section aria-labelledby="subdomain-detail-heading">
      {/* Subdomain Metadata Header */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <h2 id="subdomain-detail-heading" className="text-2xl font-semibold text-foreground">
            {subdomain.subdomainLabel}
          </h2>
          <dl className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            <div className="flex items-center gap-2">
              <dt className="text-muted-foreground">FQDN:</dt>
              <dd className="font-mono text-xs">{subdomain.fqdn}</dd>
            </div>
            <div className="flex items-center gap-2">
              <dt className="text-muted-foreground">Parent Domain:</dt>
              <dd>{parentDomainName}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Assignments Section */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Assignments</h3>
        <Button onClick={handleShowAssignForm} disabled={showAssignForm} size="sm">
          Assign User
        </Button>
      </div>

      {/* User Autocomplete Assignment Form */}
      {showAssignForm && (
        <div className="mb-4 rounded-md border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Search and select a user to assign</span>
            <Button variant="ghost" size="sm" onClick={handleHideAssignForm}>
              Cancel
            </Button>
          </div>
          <UserAutocomplete
            onSelect={handleUserSelect}
            onClear={handleUserClear}
          />
        </div>
      )}

      {/* Assignment List */}
      {assignmentState.isLoading ? (
        <Spinner label="Loading assignments..." />
      ) : assignmentState.error ? (
        <ErrorState message={assignmentState.error} onRetry={handleRetryAssignments} />
      ) : assignmentState.assignments.length === 0 ? (
        <EmptyState
          title="No assignments"
          description="This subdomain does not have any user assignments yet. Assign the first user to get started."
          actionLabel="Assign User"
          onAction={handleShowAssignForm}
        />
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">User</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Assigned By</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Assigned Date</th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {assignmentState.assignments.map((assignment) => (
                <tr key={`${assignment.subdomainId}-${assignment.userId}`} className="border-b">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="font-medium">
                          {getDisplayName(assignment)}
                          {isRowResolving(assignment) && (
                            <span className="ml-2 inline-block h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" aria-label="Loading user details" />
                          )}
                        </div>
                        {getSecondaryText(assignment) && (
                          <div className="text-xs text-muted-foreground">
                            {getSecondaryText(assignment)}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {assignment.assignedBy.includes('@')
                      ? assignment.assignedBy
                      : resolvedUsers[assignment.assignedBy]?.displayName ?? assignment.assignedBy}
                  </td>
                  <td className="px-4 py-3">{new Date(assignment.assignedAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleRemoveClick(assignment.userId, assignment.email)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirmation Dialog for Remove */}
      <ConfirmationDialog
        open={confirmRemove !== null}
        title="Remove Assignment"
        description={`Are you sure you want to remove the assignment for ${confirmRemove?.email ?? 'this user'} from ${subdomain.fqdn}?`}
        confirmLabel="Remove"
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
        destructive
      />
    </section>
  );
}
